import { useMutation } from '@platform/api-client';
import { Combobox } from '@platform/design-system';
import type { RequestBody, Operation, TypedClient } from '@platform/openapi';
import type { CatalogOption, OptionIndex, OptionValue } from '../options.ts';
import { countryChoices, originOptionOf } from '../origin.ts';
import { useOriginLabel } from '../OriginBadge.tsx';
import { usePageContext } from '../../usePageContext.ts';
import { chosenValuesOf, withOriginToggled } from './option-axes.ts';
import styles from './ProductEditor.module.css';
import { useOptionAxes } from './useOptionAxes.ts';

type OriginBody = RequestBody<Operation<'/admin/product-origins', 'post'>>;

type Toggled = Pick<OptionValue, 'id' | 'optionId' | 'code'>;

const NO_COUNTRY = '';

export type OriginFieldProps = {
  api: TypedClient;
  options: readonly CatalogOption[];
  index: OptionIndex;
};

export const OriginField = ({ api, options, index }: OriginFieldProps) => {
  const { t, locale, describeError, invalidate } = usePageContext();
  const { axes, apply } = useOptionAxes(index);
  const labelOf = useOriginLabel();
  const origin = originOptionOf(options);
  const chosenIds = origin === undefined ? [] : chosenValuesOf(axes, origin.id);
  const chosen = origin?.values.filter((value) => chosenIds.includes(value.id)) ?? [];
  const local = origin?.values.find((value) => value.countryCode === null);
  const isLocal = chosen.some((value) => value.countryCode === null);
  const imported = chosen.filter((value) => value.countryCode !== null);

  const toggle = (value: Toggled) => {
    apply(withOriginToggled(axes, value.optionId, value.id), new Map([[value.id, value.code]]));
  };

  const create = useMutation({
    mutationFn: (body: OriginBody) => api.post('/admin/product-origins', { body }),
    onSuccess: async (value) => {
      await invalidate('options');
      toggle(value);
    },
  });

  const chooseCountry = (countryCode: string) => {
    if (countryCode === NO_COUNTRY) return;
    const known = origin?.values.find((value) => value.countryCode === countryCode);
    if (known === undefined) create.mutate({ kind: 'imported', countryCode });
    else toggle(known);
  };

  return (
    <fieldset className={styles['picker']} data-chosen={chosen.length > 0}>
      <legend className={styles['pickerHead']}>
        <span className={styles['pickerName']}>{t('productEditor.origin')}</span>
        <span className={styles['pickerCount']}>{t('productEditor.originHelp')}</span>
      </legend>
      <div className={styles['chips']} role="group" aria-label={t('productEditor.origin')}>
        <button
          type="button"
          className={styles['chip']}
          aria-pressed={isLocal}
          disabled={create.isPending}
          onClick={() => {
            if (local === undefined) create.mutate({ kind: 'local' });
            else toggle(local);
          }}
        >
          {t('origin.local')}
        </button>
        {imported.map((value) => (
          <button
            key={value.id}
            type="button"
            className={styles['chip']}
            aria-pressed
            aria-label={t('productEditor.originRemove', { origin: labelOf(value) })}
            onClick={() => {
              toggle(value);
            }}
          >
            {labelOf(value)}
          </button>
        ))}
      </div>
      <Combobox
        label={t('productEditor.originCountry')}
        value={NO_COUNTRY}
        searchPlaceholder={t('productEditor.originFindCountry')}
        noMatchLabel={t('productEditor.originNoCountry')}
        options={[
          { value: NO_COUNTRY, label: t('productEditor.originAddCountry') },
          ...countryChoices(locale, new Set(imported.flatMap((value) => value.countryCode ?? []))),
        ]}
        onChange={chooseCountry}
      />
      {chosen.length > 1 ? <p className={styles['help']}>{t('productEditor.originSeveral')}</p> : null}
      {create.error === null ? null : (
        <p role="alert" className={styles['fieldError']}>
          {describeError(create.error)}
        </p>
      )}
    </fieldset>
  );
};
